import { useState, useEffect, useMemo, useCallback } from "react";
import { useSearchParams } from "react-router-dom";

// Фильтры списка, которые запоминаются так же, как сортировка и страница в ProviderList: применённые
// значения живут в URL (?status=ok&node_id=3…) — переживают «Назад», перезагрузку, ссылку, а ссылка с
// другой страницы (/screenshots?device_id=N) сразу открывает список с фильтром.
//
// keys — query-параметры фильтров (те же имена, что ждёт бэкенд). Не должны совпадать с page /
// orderBy / orderBySort — их ведёт ProviderList. ProviderList правит свои параметры через
// new URLSearchParams(previous), так что фильтры переживают и сортировку, и пагинацию.
//
// values / setValue — поля формы: меняются на каждый ввод, в запрос не уходят. applied — то, что в
// URL, его и отдают в <List searchParams>. apply / reset — «Применить» / «Сбросить»: пишут в URL и
// сбрасывают страницу (на пятой странице другого фильтра делать нечего).
export default function useListFilters(keys) {
    const [urlSearchParams, setUrlSearchParams] = useSearchParams();
    const keysKey = keys.join(',');

    const appliedKey = keys.map((key) => urlSearchParams.get(key) ?? '').join('\u0000');
    const applied = useMemo(
        () => Object.fromEntries(keys.map((key) => [key, urlSearchParams.get(key) ?? ''])),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [keysKey, appliedKey],
    );

    const [values, setValues] = useState(applied);

    // Пришли по ссылке или «Назад» с другими параметрами — подтянуть их в поля, иначе в форме
    // осталось бы прежнее, а список показывал новое.
    useEffect(() => { setValues(applied); }, [applied]);

    const setValue = useCallback((key, value) => {
        setValues((previous) => ({ ...previous, [key]: value }));
    }, []);

    const writeUrl = useCallback((nextValues) => {
        const next = new URLSearchParams(urlSearchParams);
        next.delete('page');
        keys.forEach((key) => {
            const value = nextValues[key];
            if ((value !== undefined) && (value !== null) && (value !== '')) next.set(key, value);
            else next.delete(key);
        });
        // Тот же адрес — не пишем: каждая запись добавляет шаг истории, и «Назад» после повторного
        // «Применить» с теми же значениями пришлось бы жать дважды.
        if (next.toString() === urlSearchParams.toString()) return;
        setUrlSearchParams(next);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [urlSearchParams, setUrlSearchParams, keysKey]);

    const apply = useCallback(() => writeUrl(values), [writeUrl, values]);
    const reset = useCallback(() => {
        const empty = Object.fromEntries(keys.map((key) => [key, '']));
        setValues(empty);
        writeUrl(empty);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [writeUrl, keysKey]);

    return { values, setValue, applied, apply, reset };
}
